import { MenuIcon } from "@edge-web-ui/edge-icons";
import { Canvas, Container, Pane, PaneResizeDirection, Row, } from "@microsoft/fast-layouts-react";
import { Background, Button, ButtonAppearance, Heading, HeadingSize, HeadingTag, } from "@microsoft/fast-components-react-msft";
import { HighContrastColor, highContrastOptOutProperty, highContrastSelector, neutralLayerL1, neutralOutlineActive, neutralOutlineHover, } from "@microsoft/fast-components-styles-msft";
import Foundation from "@microsoft/fast-components-foundation-react";
import ReactDOM from "react-dom";
import React from "react";
import { canUseDOM } from "exenv-es6";
import { get } from "lodash-es";
import { classNames, Direction, KeyCodes } from "@microsoft/fast-web-utilities";
import { applyFocusVisible } from "@microsoft/fast-jss-utilities";
const rowStyle = {
    row_resizeHandle: Object.assign({ border: "none", background: "none", transition: "all 0.4s ease-in-out", "&::after": {
            content: '""',
            position: "absolute",
            top: "4px",
            left: "0",
            width: "100%",
            height: "1px",
            background: neutralOutlineHover,
            [highContrastSelector]: {
                background: HighContrastColor.buttonText,
            },
        }, "&:hover": {
            opacity: "1",
            transform: "scaleX(1)",
            cursor: "row-resize",
        }, "&:active": {
            "&::after": {
                background: neutralOutlineActive,
                [highContrastSelector]: {
                    background: HighContrastColor.buttonText,
                    height: "2px",
                },
            },
        } }, applyFocusVisible({
        [highContrastSelector]: {
            background: HighContrastColor.buttonText,
            height: "1px",
        },
    }), { [highContrastSelector]: Object.assign({}, highContrastOptOutProperty, { opacity: "1" }) }),
};
const paneStyle = {
    pane_resizeHandle: Object.assign({ border: "none", background: "none", transition: "all 0.4s ease-in-out", "&::after": {
            content: '""',
            position: "absolute",
            bottom: "0",
            left: "4px",
            width: "1px",
            height: "100%",
            background: neutralOutlineHover,
            [highContrastSelector]: {
                background: HighContrastColor.buttonText,
            },
        }, "&:hover": {
            opacity: "1",
            transform: "scaleX(1)",
            cursor: "col-resize",
        }, "&:active": {
            "&::after": {
                background: neutralOutlineActive,
                [highContrastSelector]: {
                    background: HighContrastColor.buttonText,
                    width: "2px",
                },
            },
        } }, applyFocusVisible({
        [highContrastSelector]: {
            background: HighContrastColor.buttonText,
            width: "1px",
        },
    }), { [highContrastSelector]: Object.assign({}, highContrastOptOutProperty, { opacity: "1" }) }),
};
const buttonStyle = {
    button: {
        minWidth: "32px",
    },
};
/* tslint:disable-next-line */
export default class SidePaneLayout extends Foundation {
    constructor(props) {
        super(props);
        this.handledProps = {
            title: void 0,
            textField: void 0,
            lightDismissEnabled: void 0,
            mobileNavigationMenuButtonLabel: void 0,
            mobileNavigationMenuButtonProps: void 0,
            globalActionsButtonLabel: void 0,
            globalActionsButtonProps: void 0,
            navigationMenu: void 0,
            globalActions: void 0,
            paneMinWidth: void 0,
            mobileNavBackgroundValue: void 0,
            managedClasses: void 0,
            inPageFlow: void 0,
            resizable: void 0,
            mobileNavWindowMaxWidth: void 0,
            mobileNavOpenGlyph: void 0,
            mobileNavCloseGlyph: void 0,
        };
        this.navPaneElement = React.createRef();
        this.openMenuButtonElement = React.createRef();
        this.closeMenuButtonElement = React.createRef();
        this.direction = Direction.ltr;
        this.handleWindowKeyDown = (event) => {
            if (this.state.navOpen && event.keyCode === KeyCodes.escape) {
                this.resetMenus();
                this.focusHamburgerMenuButton();
            }
        };
        this.toggleGlobalActions = (e) => {
            if (typeof get(this.props, "globalActionsButtonProps.onClick") === "function") {
                this.props.globalActionsButtonProps.onClick(e);
            }
            this.setState({
                menuOpen: !this.state.menuOpen,
            });
        };
        this.handleResize = () => {
            if (canUseDOM()) {
                const isResponsive = window.innerWidth <= this.props.mobileNavWindowMaxWidth;
                if (isResponsive !== this.state.isResponsive) {
                    this.resetMenus();
                }
                this.setState({
                    isResponsive,
                });
            }
        };
        this.handleMobileNavigationMenuVisibilityToggle = (e) => {
            if (typeof get(this.props, "mobileNavigationMenuButtonProps.onClick") ===
                "function") {
                this.props.mobileNavigationMenuButtonProps.onClick(e);
            }
            this.toggleMobileMenu();
        };
        this.toggleMobileMenu = () => {
            this.setState({
                navOpen: !this.state.navOpen,
            }, () => {
                const element = ReactDOM.findDOMNode(this.navPaneElement.current);
                if (this.state.navOpen && element instanceof HTMLElement) {
                    element.style.display = "flex";
                }
                this.focusHamburgerMenuButton();
                if (element instanceof HTMLElement) {
                    element.addEventListener("animationend", this.handleNavAnimationEnd);
                }
            });
        };
        this.handleNavAnimationEnd = (e) => {
            const element = ReactDOM.findDOMNode(this.navPaneElement.current);
            if (element instanceof HTMLElement) {
                element.removeEventListener("animationend", this.handleNavAnimationEnd);
                if (!this.state.navOpen) {
                    element.style.display = "none";
                }
            }
        };
        /**
         *  Updates the direction
         */
        this.updateDirection = () => {
            if (this.rootElement.current === null) {
                return;
            }
            const closest = this.rootElement.current.closest(`[dir]`);
            if (closest === null) {
                this.direction = Direction.ltr;
                return;
            }
            this.direction =
                closest.getAttribute("dir") === "rtl" ? Direction.rtl : Direction.ltr;
        };
        this.state = {
            navOpen: false,
            menuOpen: false,
            isResponsive: false,
        };
        window.addEventListener("resize", this.handleResize);
        this.rootElement = React.createRef();
    }
    /**
     * React life-cycle method
     */
    componentWillUpdate() {
        this.updateDirection();
    }
    /**
     * React life-cycle method
     */
    componentDidMount() {
        this.handleResize();
        if (canUseDOM()) {
            window.addEventListener("keydown", this.handleWindowKeyDown);
        }
    }
    /**
     * React life-cycle method
     */
    componentWillUnmount() {
        if (canUseDOM()) {
            window.removeEventListener("keydown", this.handleWindowKeyDown);
            window.removeEventListener("resize", this.handleResize);
        }
    }
    render() {
        const { sidePaneLayout_canvas, sidePaneLayout_container, sidePaneLayout_row, } = this.props.managedClasses;
        return (React.createElement("div", Object.assign({ ref: this.rootElement }, this.unhandledProps(), { className: this.generateClassNames() }),
            React.createElement(Container, { className: classNames(sidePaneLayout_container) },
                React.createElement(Row, { className: classNames(sidePaneLayout_row), jssStyleSheet: rowStyle, fill: true },
                    this.renderLayout(),
                    React.createElement(Canvas, { className: classNames(sidePaneLayout_canvas) }, this.props.children),
                    this.renderLightDismiss()))));
    }
    /**
     * Create class names
     */
    generateClassNames() {
        const { sidePaneLayout, sidePaneLayout__inPageFlow, } = this.props.managedClasses;
        return super.generateClassNames(classNames(sidePaneLayout, [
            sidePaneLayout__inPageFlow,
            this.props.inPageFlow,
        ]));
    }
    /**
     * Create flyout class names
     */
    generateMobileNavMenuClassNames() {
        const { sidePaneLayout_mobileNavigationMenu, sidePaneLayout_mobileNavigationMenu__open, sidePaneLayout_mobileNavigationMenu__close, } = this.props.managedClasses;
        const isOpen = !!this.state.navOpen;
        return classNames(sidePaneLayout_mobileNavigationMenu, [sidePaneLayout_mobileNavigationMenu__open, isOpen], [sidePaneLayout_mobileNavigationMenu__close, !isOpen]);
    }
    /**
     * Create pane class names
     */
    generatePaneClassNames() {
        return classNames(this.props.managedClasses.sidePaneLayout_pane);
    }
    renderLayout() {
        const { sidePaneLayout_navigation, sidePaneLayout_mobileNavigation, sidePaneLayout_mobileNavigation_container, sidePaneLayout_mobileNavigation_headerContainer, sidePaneLayout_navigation_headerContainer, } = this.props.managedClasses;
        if (this.state.isResponsive) {
            return (React.createElement("div", null,
                React.createElement(Background, { value: this.props.mobileNavBackgroundValue, className: this.generateMobileNavMenuClassNames(), "aria-hidden": !this.state.navOpen, ref: this.navPaneElement },
                    React.createElement("div", { className: classNames(sidePaneLayout_navigation_headerContainer) },
                        React.createElement("div", { className: classNames(sidePaneLayout_mobileNavigation_headerContainer) },
                            this.renderHamburgerButton(this.closeMenuButtonElement),
                            this.renderHeading()),
                        this.renderGlobalActionsButton()),
                    React.createElement("div", { className: classNames(sidePaneLayout_mobileNavigation_container) }, this.renderNavigationMenu(classNames(sidePaneLayout_mobileNavigation), this.toggleMobileMenu))),
                React.createElement("div", { className: this.generatePaneClassNames() },
                    React.createElement("div", { className: classNames(sidePaneLayout_navigation_headerContainer) }, this.renderHeader()),
                    this.renderTextField())));
        }
        return (React.createElement(Pane, { resizable: this.props.resizable, resizeFrom: this.direction === Direction.ltr
                ? PaneResizeDirection.east
                : PaneResizeDirection.west, minWidth: this.props.paneMinWidth, jssStyleSheet: paneStyle },
            React.createElement("div", { className: this.generatePaneClassNames() },
                React.createElement("div", { className: classNames(sidePaneLayout_navigation_headerContainer) },
                    this.renderHeader(),
                    this.renderGlobalActionsButton()),
                this.renderTextField(),
                this.renderNavigationMenu(classNames(sidePaneLayout_navigation)))));
    }
    /**
     * Resets Menus When window resizes.
     */
    resetMenus() {
        this.setState({
            navOpen: false,
            menuOpen: false,
        });
    }
    renderGlobalActionsButton() {
        const { sidePaneLayout_globalActionsButton, sidePaneLayout_mobileNavigationMenu_buttonContainer, } = this.props.managedClasses;
        if (this.props.globalActions) {
            return (React.createElement("div", { className: classNames(sidePaneLayout_mobileNavigationMenu_buttonContainer) },
                React.createElement(Button, Object.assign({}, this.props.globalActionsButtonProps, { jssStyleSheet: buttonStyle, appearance: ButtonAppearance.stealth, "aria-label": this.props.globalActionsButtonLabel || null, className: classNames(sidePaneLayout_globalActionsButton), onClick: this.toggleGlobalActions }), this.generateIcon("more")),
                this.showGlobalActions()));
        }
        else {
            return null;
        }
    }
    renderHamburgerButton(button) {
        return (React.createElement(Button, Object.assign({}, this.props.mobileNavigationMenuButtonProps, { jssStyleSheet: buttonStyle, appearance: ButtonAppearance.stealth, "aria-label": this.props.mobileNavigationMenuButtonLabel || null, "aria-haspopup": this.state.navOpen ? null : true, className: classNames(this.props.managedClasses.sidePaneLayout_mobileNavigationMenu_button), onClick: this.handleMobileNavigationMenuVisibilityToggle, ref: button }), this.state.navOpen
            ? this.renderGlyph(this.props.mobileNavCloseGlyph)
            : this.renderGlyph(this.props.mobileNavOpenGlyph)));
    }
    renderGlyph(glyph) {
        return typeof glyph === "function"
            ? glyph(classNames(this.props.managedClasses.sidePaneLayout_icon))
            : null;
    }
    generateIcon(title) {
        return (React.createElement(MenuIcon, { titleText: title, className: classNames(this.props.managedClasses.sidePaneLayout_icon) }));
    }
    renderLightDismiss() {
        if (this.state.navOpen && this.props.lightDismissEnabled) {
            return (React.createElement("div", { style: {
                    position: "absolute",
                    border: "none",
                    inset: "0",
                    top: "0",
                    left: "0",
                    right: "0",
                    bottom: "0",
                }, onClick: this.handleMobileNavigationMenuVisibilityToggle }));
        }
    }
    renderHeading(styleOverrides = false) {
        const styleSheet = {
            heading_4: { marginTop: "10px" },
        };
        return (React.createElement(Heading, { className: classNames(this.props.managedClasses.sidePaneLayout_navigation_headerText), jssStyleSheet: styleOverrides ? styleSheet : null, tag: HeadingTag.h1, size: HeadingSize._4 }, this.props.title));
    }
    renderHeader() {
        return (React.createElement("div", { className: this.props.managedClasses.sidePaneLayout_navigation_header },
            this.renderHamburgerButton(this.openMenuButtonElement),
            this.renderHeading(true)));
    }
    showGlobalActions() {
        if (this.state.menuOpen) {
            return typeof this.props.globalActions === "function"
                ? this.props.globalActions(classNames(this.props.managedClasses.sidePaneLayout_globalActions))
                : null;
        }
    }
    renderTextField() {
        return typeof this.props.textField === "function"
            ? this.props.textField(classNames(this.props.managedClasses.sidePaneLayout_textField))
            : null;
    }
    renderNavigationMenu(className, dismissMobileMenu) {
        return typeof this.props.navigationMenu === "function"
            ? this.props.navigationMenu(className, dismissMobileMenu)
            : null;
    }
    /**
     * Focus the other hamburger menu button when hamburger menu is clicked
     */
    focusHamburgerMenuButton() {
        if (this.state.navOpen) {
            if (this.closeMenuButtonElement && this.closeMenuButtonElement.current) {
                ReactDOM.findDOMNode(this.closeMenuButtonElement.current).focus();
            }
        }
        else {
            if (this.openMenuButtonElement && this.openMenuButtonElement.current) {
                ReactDOM.findDOMNode(this.openMenuButtonElement.current).focus();
            }
        }
    }
}
SidePaneLayout.defaultProps = {
    title: "Side Pane Title",
    lightDismissEnabled: true,
    mobileNavBackgroundValue: neutralLayerL1,
    paneMinWidth: 406,
    inPageFlow: false,
    managedClasses: {},
    resizable: true,
    mobileNavWindowMaxWidth: 1035,
    mobileNavCloseGlyph: (className) => (React.createElement(MenuIcon, { titleText: "menu", className: className })),
    mobileNavOpenGlyph: (className) => (React.createElement(MenuIcon, { titleText: "menu", className: className })),
};
